import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CRow,
} from "@coreui/react";
import { useCallback, useEffect, useState } from "react";
import { BsFillPlusCircleFill } from "react-icons/bs";
import { FaBook } from "react-icons/fa";
import { getAbsoluteUrl } from "../../api/urls";
import { getTaxPayerApiKeyList } from "../../api/taxpayer-api-keys";
import TaxPayerApiKey from "../../models/taxpayer-api-key";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { SUCCESS } from "../../utils/constants/tags";
import { successAlert, warningAlert } from "../utils/messages";
import TaxPayerApiKeyUpdateModal from "./TaxPayerApiKeyUpdateModal";
import TaxPayerApiKeyEditModal from "./TaxPayerApiKeyEditModal";
import TaxPayerApiKeyDeleteModal from "./TaxPayerApiKeyDeleteModal";
import TaxPayerApiKeyCrudActions from "./TaxPayerApiKeyCrudActions";
import TaxPayerApiKeyRawKeyModal from "./TaxPayerApiKeyRawKeyModal";

const TaxPayerApiKeyCrud = () => {
  const fields = [
    { key: "keyName", _classes: ["text-center"], label: "Nombre" },
    { key: "keyPrefix", _classes: ["text-center"], label: "Prefijo" },
    { key: "permissions", _classes: ["text-center"], label: "Permisos" },
    { key: "expiresAt", _classes: ["text-center"], label: "Expira" },
    { key: "createdAt", _classes: ["text-center"], label: "Creada" },
    {
      key: "actions",
      _classes: ["text-center"],
      label: "Acciones",
      filter: false,
    },
  ];

  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer
  );

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showRawKeyModal, setShowRawKeyModal] = useState(false);
  const [selectedApiKey, setSelectedApiKey] = useState<TaxPayerApiKey | undefined>(undefined);
  const [newRawKey, setNewRawKey] = useState("");
  const [newKeyName, setNewKeyName] = useState("");

  const [loading, setLoading] = useState(true);
  const [apiKeys, setApiKeys] = useState<TaxPayerApiKey[]>([]);

  const fetchApiKeys = useCallback(async () => {
    setLoading(true);
    const status = await getTaxPayerApiKeyList(taxPayer.id!);
    if (status.status === SUCCESS) {
      if (status.data !== undefined) {
        setApiKeys(status.data.items);
      }
    } else {
      const message = status.detail ?? "Error desconocido";
      warningAlert(message);
    }
    setLoading(false);
  }, [taxPayer]);

  const onAddClick = () => {
    setShowCreateModal(true);
  };

  const onEditClick = (apiKey: TaxPayerApiKey) => {
    setSelectedApiKey(apiKey);
    setShowEditModal(true);
  };

  const onRevokeClick = (apiKey: TaxPayerApiKey) => {
    setSelectedApiKey(apiKey);
    setShowRevokeModal(true);
  };

  const onCreateCancel = () => {
    setShowCreateModal(false);
  };

  const onCreateSuccess = (created?: TaxPayerApiKey) => {
    setShowCreateModal(false);
    if (created && (created as any).rawKey) {
      setNewRawKey((created as any).rawKey);
      setNewKeyName(created.keyName ?? "");
      setShowRawKeyModal(true);
    } else {
      successAlert("API Key creada con éxito!");
    }
    fetchApiKeys();
  };

  const onEditCancel = () => {
    setShowEditModal(false);
    setSelectedApiKey(undefined);
  };

  const onEditSuccess = () => {
    successAlert("API Key actualizada con éxito!");
    setShowEditModal(false);
    setSelectedApiKey(undefined);
    fetchApiKeys();
  };

  const onRevokeCancel = () => {
    setShowRevokeModal(false);
  };

  const onRevokeSuccess = () => {
    successAlert("API Key revocada con éxito!");
    setShowRevokeModal(false);
    fetchApiKeys();
  };

  const onRawKeyModalClose = () => {
    setShowRawKeyModal(false);
    setNewRawKey("");
    setNewKeyName("");
  };

  useEffect(() => {
    fetchApiKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayer]);

  const docsUrl = getAbsoluteUrl("/api/v2/docs");

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CAlert color="info" className="d-flex align-items-center">
            <FaBook className="mr-2 flex-shrink-0" />
            <span>
              Integrá tu sistema con nuestra API pública.{" "}
              <a href={docsUrl} target="_blank" rel="noopener noreferrer">
                Ver documentación de la API
              </a>
            </span>
          </CAlert>
        </CCol>
      </CRow>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2">
                <h3>API Keys</h3>
              </div>
              <div className="p-2">
                <CButton
                  color="primary"
                  className="float-right"
                  onClick={onAddClick}
                >
                  <BsFillPlusCircleFill />
                  &nbsp; Nueva API Key
                </CButton>
              </div>
            </CCardHeader>
            <CCardBody>
              <CDataTable
                noItemsView={{
                  noItems: "No se encontraron API Keys",
                  noResults: "No se encontraron API Keys",
                }}
                addTableClasses={"table-fixed"}
                fields={fields}
                items={apiKeys}
                striped
                border
                loading={loading}
                responsive
                scopedSlots={{
                  keyName: (item: TaxPayerApiKey) => (
                    <td className="text-center">{item.keyName ?? "-"}</td>
                  ),
                  keyPrefix: (item: TaxPayerApiKey) => (
                    <td className="text-center">
                      <code>{item.keyPrefix ?? "-"}...</code>
                    </td>
                  ),
                  permissions: (item: TaxPayerApiKey) => (
                    <td className="text-center">
                      {item.permissions && item.permissions.length > 0
                        ? item.permissions.join(", ")
                        : "-"}
                    </td>
                  ),
                  expiresAt: (item: TaxPayerApiKey) => (
                    <td className="text-center">
                      {item.expiresAt
                        ? new Date(item.expiresAt).toLocaleDateString()
                        : "Nunca"}
                    </td>
                  ),
                  createdAt: (item: TaxPayerApiKey) => (
                    <td className="text-center">
                      {item.createdAt
                        ? new Date(item.createdAt).toLocaleDateString()
                        : "-"}
                    </td>
                  ),
                  actions: (item: TaxPayerApiKey) => (
                    <TaxPayerApiKeyCrudActions
                      apiKey={item}
                      onEditClick={onEditClick}
                      onRevokeClick={onRevokeClick}
                    />
                  ),
                }}
              />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <TaxPayerApiKeyUpdateModal
        show={showCreateModal}
        onCancel={onCreateCancel}
        onSuccess={onCreateSuccess}
      />
      <TaxPayerApiKeyEditModal
        show={showEditModal}
        apiKey={selectedApiKey}
        onCancel={onEditCancel}
        onSuccess={onEditSuccess}
      />
      <TaxPayerApiKeyDeleteModal
        show={showRevokeModal}
        apiKey={selectedApiKey}
        onCancel={onRevokeCancel}
        onSuccess={onRevokeSuccess}
      />
      <TaxPayerApiKeyRawKeyModal
        show={showRawKeyModal}
        rawKey={newRawKey}
        keyName={newKeyName}
        onClose={onRawKeyModalClose}
      />
    </>
  );
};

export default TaxPayerApiKeyCrud;
